import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Control Room is a different origin from Accounts in every environment. Resolve it from
// configuration per environment and never silently fall back to our own origin — that is
// what sent production sign-in to accounts.1os.in/api/auth/login and got a 404.
// Exact Accounts hostname -> which Control Room variable owns sign-in. A hostname that is
// not listed is not assumed to be production: it yields a visible configuration error.
const CONTROL_ROOM_VAR_BY_HOST = {
  'accounts.1os.in': 'REACT_APP_CONTROL_ROOM_URL_PRODUCTION',
  'driver-insights-hub.preview.emergentagent.com': 'REACT_APP_CONTROL_ROOM_URL',
  localhost: 'REACT_APP_CONTROL_ROOM_URL',
  '127.0.0.1': 'REACT_APP_CONTROL_ROOM_URL',
};

const resolveControlRoomUrl = () => {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const varName = CONTROL_ROOM_VAR_BY_HOST[host];
  if (!varName) return null;
  const candidate = varName === 'REACT_APP_CONTROL_ROOM_URL_PRODUCTION'
    ? process.env.REACT_APP_CONTROL_ROOM_URL_PRODUCTION
    : process.env.REACT_APP_CONTROL_ROOM_URL;
  if (!candidate) return null;
  const url = candidate.replace(/\/$/, '');
  // A Control Room URL equal to our own backend is the build-rewrite defect, not an address.
  if (url === (BACKEND_URL || '').replace(/\/$/, '')) return null;
  return url;
};

const CONTROL_ROOM_URL = resolveControlRoomUrl();
export const SIGN_IN_CONFIGURED = Boolean(CONTROL_ROOM_URL);
const SIGN_IN_NOT_CONFIGURED =
  'Sign-in is not configured for this environment. Accounts cannot reach Control Room. '
  + 'Nothing was changed — please tell your administrator.';
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Envelope unwrappers — operator pages must never crash when the
 * backend wraps a list in a Truth-Contract envelope
 * (e.g. `{state, source, vendors:[...]}` or
 * `{state, bills:[...], total, ...}`). These helpers always return
 * an array, regardless of envelope shape, EC availability, or
 * legacy flat-array responses.
 *
 * They intentionally do NOT consume `state`/`source` — call-sites
 * that want to render PARTIAL/UNAVAILABLE banners should read
 * those fields off `response.data` directly while still using these
 * helpers to get a render-safe array for `.map()`/`.filter()`.
 */

/**
 * normalizeVendor — central schema-mapping layer for Expense Capture
 * vendor objects.
 *
 * EC's canonical vendor shape (`vendor_id`, `vendor_name`,
 * `vendor_gst_number`, `vendor_category`, `vendor_phone`,
 * `vendor_email`, …) is preserved verbatim AND aliased to the
 * legacy frontend shape (`id`, `name`, `gstin`, `category`, `phone`,
 * `email`). Both consumers — components that already read the
 * canonical EC fields (DashboardPage, PnLPage, CashFlowPage, etc.)
 * and components written against the legacy shape (VendorsPage,
 * VendorDetailPage, VendorLedgerPage, BillsPage's vendorMap, …) —
 * keep working from a single normalized object.
 *
 * Why aliases instead of a rewrite: the canonical fields
 * (`vendor_name`, …) are already used in production data-paths that
 * cross engines (Sales/Pulse/PnL); renaming them would force every
 * consumer to migrate in lockstep. Additive aliasing fixes the
 * frontend skew without touching cross-engine canon.
 */
export const normalizeVendor = (v) => {
  if (!v || typeof v !== 'object') return v;
  const id = v.vendor_id || v.id || null;
  return {
    ...v,
    id,
    vendor_id: id,                                  // ensure both keys present
    name: v.name ?? v.vendor_name ?? '',
    gstin: v.gstin ?? v.vendor_gst_number ?? '',
    category: v.category ?? v.vendor_category ?? '',
    phone: v.phone ?? v.vendor_phone ?? '',
    email: v.email ?? v.vendor_email ?? '',
  };
};

export const unwrapVendors = (data) => {
  let list;
  if (Array.isArray(data)) list = data;
  else if (Array.isArray(data?.vendors)) list = data.vendors;
  else if (Array.isArray(data?.items)) list = data.items;
  else return [];
  return list.map(normalizeVendor);
};

/**
 * normalizeBill — schema-mapping for Expense Capture bill objects.
 *
 * EC's canonical bill schema (`bill_id`, `bill_due_date`,
 * `amount_paid`, `payment_status`, …) is preserved AND aliased to
 * the frontend-legacy names that already pepper BillsPage,
 * BillAttachmentsViewer, PaymentAllocationDialog, etc.
 * (`id`, `due_date`, `paid_amount`, `balance_amount`, `status`).
 *
 * `balance_amount` is derived from `total_amount - amount_paid` so
 * the Bills row's "Balance" column reconciles even though EC
 * doesn't expose it directly. Falls back to 0 when source fields
 * are absent (the bill simply renders "₹0" for missing data — no
 * crash).
 *
 * Additive only — no canonical fields are stripped.
 */
export const normalizeBill = (b) => {
  if (!b || typeof b !== 'object') return b;
  const id = b.bill_id || b.id || null;
  const total = Number(b.total_amount) || 0;
  // EC stores paid in `amount_paid`; legacy frontend reads `paid_amount`.
  // Coerce nullable to 0 — `null - 0 → NaN` would break the balance arithmetic.
  const paid = Number(b.paid_amount ?? b.amount_paid ?? 0) || 0;
  const balance = b.balance_amount ?? Math.max(total - paid, 0);
  // EC bills carry TWO different status fields:
  //   - `status`         → document state ("CONFIRMED", "DRAFT", …)
  //   - `payment_status` → payment lifecycle ("Unpaid", "Partial", "Paid")
  // The Bills UI's StatusBadge variants are
  // {Unpaid|Partial|Paid|OVERDUE} — i.e. it expects the payment
  // lifecycle. Map the legacy `status` alias to `payment_status`,
  // and preserve the EC document status under `doc_status` so any
  // future surface that needs it isn't lost.
  return {
    ...b,
    id,
    bill_id: id,
    due_date: b.due_date ?? b.bill_due_date ?? null,
    paid_amount: paid,
    balance_amount: balance,
    status: b.payment_status ?? b.status ?? 'Unpaid',
    doc_status: b.status ?? null,
  };
};

/**
 * normalizePayment — schema-mapping for the Accounts vendor-payment
 * surface. The Accounts engine intentionally has NO local payment
 * ledger; the records returned by `/vendors/:id/payments` are the
 * `sync_back_events.BILL_PAID_CALLBACK` audit trail (Accounts → EC
 * mark-paid round-trips).
 *
 * The legacy VendorDetailPage table was written against a richer
 * payment-ledger shape (`payment_id`, `payment_date`,
 * `reference_no`, `payment_method`, `amount`, `allocated_amount`,
 * `unallocated_amount`, `auto_created`, `proof_status`). This
 * normalizer maps the audit-event fields onto those aliases so the
 * page renders sane values when callback events do exist, instead
 * of a row of `undefined`s.
 *
 * Audit-only nature is preserved via `audit_only: true` flag and
 * `proof_status: 'AUDIT'` so the proof-attach UI doesn't try to
 * upload to a non-existent payment.
 */
export const normalizePayment = (p) => {
  if (!p || typeof p !== 'object') return p;
  // Synthetic stable id — these audit events have no payment_id of
  // their own. Fall back to a deterministic composite so React keys
  // stay stable across re-renders.
  const synthId =
    p.payment_id ||
    p.event_id ||
    (p.bill_id && p.timestamp ? `${p.bill_id}:${p.timestamp}` : null) ||
    p.timestamp ||
    null;
  const amount = Number(p.amount ?? p.amount_paid ?? 0) || 0;
  return {
    ...p,
    payment_id: synthId,
    payment_date: p.payment_date ?? p.paid_at ?? p.timestamp ?? null,
    reference_no: p.reference_no ?? p.payment_reference ?? '',
    payment_method: p.payment_method ?? 'CALLBACK',
    amount,
    allocated_amount: Number(p.allocated_amount ?? amount) || 0,
    unallocated_amount: Number(p.unallocated_amount ?? 0) || 0,
    auto_created: p.auto_created ?? p.paid_by === 'ACCOUNTS_ENGINE',
    proof_status: p.proof_status ?? 'AUDIT',
    audit_only: true,
  };
};

export const unwrapBills = (data) => {
  let list;
  if (Array.isArray(data)) list = data;
  else if (Array.isArray(data?.bills)) list = data.bills;
  else if (Array.isArray(data?.items)) list = data.items;
  else return [];
  return list.map(normalizeBill);
};

export const unwrapPayments = (data) => {
  let list;
  if (Array.isArray(data)) list = data;
  else if (Array.isArray(data?.payments)) list = data.payments;
  else if (Array.isArray(data?.items)) list = data.items;
  else return [];
  return list.map(normalizePayment);
};

/** Stable extractor for source/state metadata returned alongside a list. */
export const extractSourceMeta = (data) => {
  if (!data || Array.isArray(data) || typeof data !== 'object') {
    return { state: 'VALID', source: null, reason_code: null, reason_human: null };
  }
  return {
    state: data.state || 'VALID',
    source: data.source || null,
    reason_code: data.reason_code || null,
    reason_human: data.reason_human || null,
  };
};

/**
 * Normalize a backend error into a render-safe string.
 *
 * The backend sometimes returns `detail` as a structured object — most
 * notably the `410 Gone` envelope from decommissioned endpoints
 * (`{decommissioned, reason, use_instead}`). React cannot render an
 * object as a child, so passing such a `detail` straight to
 * `toast.error()` or into JSX (`{state.error}`) crashes the page.
 *
 * Use this helper EVERYWHERE the UI surfaces an error from a network
 * call. The response interceptor below also pre-coerces
 * `error.response.data.detail` to a string defensively, so even legacy
 * call-sites that read `err.response.data.detail` directly are safe.
 */
export function normalizeApiError(error, fallback = "Operation failed") {
  const detail = error?.response?.data?.detail;
  if (detail == null) return error?.message || fallback;
  if (typeof detail === "string") return detail;
  if (typeof detail === "object") {
    return (
      detail.reason ||
      detail.message ||
      detail.error ||
      detail.use_instead ||
      JSON.stringify(detail)
    );
  }
  return String(detail);
}

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors + coerce `detail` to a render-safe string
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      window.location.href = '/login';
    }
    // Defensive: pre-coerce structured `detail` so legacy callers reading
    // `err.response.data.detail` directly still get a string. The original
    // structured payload is preserved under `error.response.data.detail_raw`
    // for any caller that genuinely wants the typed shape.
    const detail = error?.response?.data?.detail;
    if (detail && typeof detail === 'object') {
      try {
        error.response.data.detail_raw = detail;
        error.response.data.detail = (
          detail.reason ||
          detail.message ||
          detail.error ||
          detail.use_instead ||
          JSON.stringify(detail)
        );
      } catch (_e) {
        // mutation must never break the rejection path
      }
    }
    return Promise.reject(error);
  }
);

// Control Room Auth API
export const controlRoomAPI = {
  login: async (email, password, appCode = 'ACCOUNTS') => {
    if (!CONTROL_ROOM_URL) throw new Error(SIGN_IN_NOT_CONFIGURED);
    const response = await axios.post(`${CONTROL_ROOM_URL}/api/auth/login`, {
      email,
      password,
      app_code: appCode
    });
    return response;
  },
  
  getContext: async (token) => {
    if (!CONTROL_ROOM_URL) throw new Error(SIGN_IN_NOT_CONFIGURED);
    const response = await axios.get(`${CONTROL_ROOM_URL}/api/auth/context`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response;
  }
};

// Accounts API (uses Control Room token)
// Tax month selection: 'YYYY-MM' | ['YYYY-MM', ...] | params object → query params.
const taxSel = (sel) => {
  if (Array.isArray(sel)) return { months: sel.join(',') };
  if (typeof sel === 'string') return { tax_period: sel };
  return sel;
};

export const accountsAPI = {
  // Auth/Me
  getMe: () => api.get('/me'),

  // ── DECOMMISSIONED — DO NOT REINTRODUCE ──
  // The legacy `/api/sync/vendors`, `/api/sync/bills`, `/api/sync/all`
  // endpoints permanently return `410 Gone`. Vendors and bills are
  // read-through-proxied from Expense Capture; sales data is auto-synced
  // from Sales Capture via `pulse_scheduler` + startup catch-up. The
  // ONLY operator-controlled sync surface is:
  //     POST /api/accounts/sales-source/backfill
  // → exposed below as `accountsAPI.salesSourceBackfill()`.
  // syncAll / syncVendors / syncBills / syncVendorBills have been
  // removed. Build will fail if any caller still imports them.
  
  // Dashboard
  getDashboard: (params) => api.get('/accounts/dashboard', { params: typeof params === 'string' ? { period: params } : params }),
  getSpendBreakdown: (params) => api.get('/accounts/dashboard/spend-breakdown', { params: typeof params === 'string' ? { period: params } : params }),
  getSpendAnalytics: (params) => api.get('/accounts/analytics/spend', { params }),
  
  // Vendors (with outstanding)
  getVendors: (params) => api.get('/accounts/vendors', { params: typeof params === 'string' ? { org_id: params } : params }),
  getVendor: (vendorId) => api.get(`/accounts/vendors/${vendorId}`),
  getVendorBills: (vendorId, params = {}) => api.get(`/accounts/vendors/${vendorId}/bills`, { params }),
  getVendorPayments: (vendorId, params = {}) => api.get(`/accounts/vendors/${vendorId}/payments`, { params }),
  getVendorLedger: (vendorId, params = {}) => api.get(`/accounts/vendors/${vendorId}/ledger`, { params }),
  exportVendorLedgerCSV: (vendorId, params = {}) => api.get(`/accounts/exports/csv/vendor_ledger/${vendorId}`, { params, responseType: 'blob' }),
  exportVendorLedgerXLSX: (vendorId, params = {}) => api.get(`/accounts/exports/xlsx/vendor_ledger`, { params: { ...params, vendor_id: vendorId }, responseType: 'blob' }),
  exportVendorLedgerPDF: (vendorId, params = {}) => api.get(`/accounts/exports/pdf/vendor_ledger/${vendorId}`, { params, responseType: 'blob' }),
  
  // Bills
  getBills: (params) => api.get('/accounts/bills', { params }),
  checkBillDuplicates: (billId) => api.get(`/accounts/bills/${billId}/duplicates`),
  checkAllDuplicates: () => api.post('/accounts/bills/check-duplicates'),
  getBill: (billId) => api.get(`/accounts/bills/${billId}`),
  uploadBillImage: (billId, imageData) => api.post(`/accounts/bills/${billId}/image`, imageData),
  getBillAttachments: (billId) => api.get(`/accounts/bills/${billId}/attachments`),
  mapBillTax: (billId, taxMapping) => api.post(`/accounts/bills/${billId}/map-tax`, taxMapping),
  
  // Payments — DEPRECATED (Phase 3 source-hardening, May 2026).
  // Local payment ledger no longer exists. `getPayments` still works
  // as a read-only audit view of mark-paid callbacks pushed to EC.
  // WRITE endpoints below throw 410 Gone by design — DO NOT CALL:
  //   createPayment, deletePayment, allocatePayment, deleteAllocation,
  //   createPaymentFromOCR, extractPaymentProofOCR
  // Use `markBillPaid` instead — the ONE allowed write is the EC
  // mark-paid callback per bill.
  getPayments: (params) => api.get('/accounts/payments', { params }),
  getPayment: (paymentId) => api.get(`/accounts/payments/${paymentId}`),
  getUnpaidBillsForPayment: (paymentId) => api.get(`/accounts/payments/${paymentId}/unpaid-bills`),
  // Canonical bill-payment write (Phase 3+).
  markBillPaid: (billId, data, key) => api.post(`/accounts/expenses/bills/${billId}/mark-paid`, data, { headers: { 'Idempotency-Key': key } }),
  bulkMarkPaid: (data, key) => api.post('/accounts/expenses/bills/bulk-mark-paid', data, { headers: { 'Idempotency-Key': key } }),
  getPaymentCommands: (params) => api.get('/accounts/payment-commands', { params }),
  resolvePaymentCommand: (id, data) => api.post(`/accounts/payment-commands/${id}/resolve`, data),
  
  // Trial Purchases (D2)
  getTrialPurchases: (params) => api.get('/accounts/trial-purchases', { params }),
  getTrialPurchasesSummary: (period) => api.get('/accounts/trial-purchases/summary', { params: { period } }),
  reviewTrialPurchase: (entryId, data) => api.post(`/accounts/trial-purchases/${entryId}/review`, data),
  
  // Tax
  getInputTax: (params) => api.get('/accounts/tax/input', { params }),
  getOutputTax: (params) => api.get('/accounts/tax/output', { params }),
  getTaxSummary: (sel) => api.get('/accounts/tax/summary', { params: taxSel(sel) }),
  getGSTR3B: (sel) => api.get('/accounts/gst/gstr3b', { params: taxSel(sel) }),
  getTDSSummary: (sel) => api.get('/accounts/tds/summary', { params: taxSel(sel) }),
  getVatBreakdown: (sel) => api.get('/accounts/tax/vat-breakdown', { params: taxSel(sel) }),
  lockGSTPeriod: (taxPeriod) => api.post('/accounts/gst/lock', { tax_period: taxPeriod }),
  
  // GSTR-2B Reconciliation
  getGstr2bStatus: (sel) => api.get('/accounts/gstr2b/status', { params: taxSel(sel) }),
  uploadGstr2b: (formData) => api.post('/accounts/gstr2b/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  reconcileGstr2b: (taxPeriod) => api.post('/accounts/gstr2b/reconcile', { tax_period: taxPeriod }),
  
  // Credit/Debit Notes
  getCreditDebitNotes: (params) => api.get('/accounts/credit-debit-notes', { params }),
  createCreditDebitNote: (data) => api.post('/accounts/credit-debit-notes', data),
  getCreditDebitNote: (noteId) => api.get(`/accounts/credit-debit-notes/${noteId}`),
  deleteCreditDebitNote: (noteId) => api.delete(`/accounts/credit-debit-notes/${noteId}`),
  getVendorCreditDebitNotes: (vendorId) => api.get(`/accounts/vendors/${vendorId}/credit-debit-notes`),
  
  // Audit Logs
  getAuditLogs: (params) => api.get('/accounts/audit-logs', { params }),
  getAuditLog: (logId) => api.get(`/accounts/audit-logs/${logId}`),
  getAuditLogFilters: () => api.get('/accounts/audit-logs/meta/filters'),
  
  // Payment Proof OCR — DEPRECATED (Phase 3, May 2026). See markBillPaid.
  // extractPaymentProofOCR / createPaymentFromOCR removed.
  
  // P&L (Profit & Loss) Engine
  getRealtimePnL: (params) => api.get('/accounts/pnl/realtime', { params }),
  getMonthlyTargets: (params) => api.get('/accounts/pnl/targets', { params }),
  setMonthlyTargets: (data) => api.put('/accounts/pnl/targets', data),
  getTargetVariance: (params) => api.get('/accounts/pnl/target-variance', { params }),
  // Target Admin (Settings / Targets page)
  listTargets: (params) => api.get('/accounts/targets', { params }),
  upsertTarget: (data) => api.put('/accounts/targets', data),
  deleteTarget: (month) => api.delete(`/accounts/targets/${month}`),
  // Financial Obligations (Phase 0 — Operating only)
  listObligations: (params) => api.get('/accounts/obligations', { params }),
  getObligationsSummary: () => api.get('/accounts/obligations/summary'),
  getObligationCategories: () => api.get('/accounts/obligations/categories'),
  createObligation: (data) => api.post('/accounts/obligations', data),
  updateObligation: (id, data) => api.put(`/accounts/obligations/${id}`, data),
  markObligationPaid: (id, data, key) => api.post(`/accounts/obligations/${id}/mark-paid`, data, { headers: { 'Idempotency-Key': key } }),
  deleteObligation: (id) => api.delete(`/accounts/obligations/${id}`),
  getObligation: (id) => api.get(`/accounts/obligations/${id}`),
  reverseObligationPayment: (id, paymentId, reason) => api.post(`/accounts/obligations/${id}/payments/${encodeURIComponent(paymentId)}/reverse`, { reason }),
  cancelObligation: (id, reason) => api.post(`/accounts/obligations/${id}/cancel`, { reason }),
  getObligationRecognition: (params) => api.get('/accounts/obligations/recognition', { params }),
  getObligationForecast: (params) => api.get('/accounts/obligations/forecast', { params }),
  getIndependentCash: (params) => api.get('/accounts/cash/independent-summary', { params }),
  getMyPermissions: () => api.get('/accounts/me/permissions'),
  getSalesCompleteness: (params) => api.get('/accounts/sales-projection/completeness', { params }),
  repairSalesProjection: (data) => api.post('/accounts/sales-projection/repair', data),
  listSalesRepairJobs: () => api.get('/accounts/sales-projection/jobs'),
  resumeSalesRepairJob: (id) => api.post(`/accounts/sales-projection/jobs/${id}/resume`),
  getPnLCategories: () => api.get('/accounts/pnl/categories'),
  getInventoryHealth: () => api.get('/accounts/integrations/inventory/health'),
  publishPnL: (data) => api.post('/accounts/pnl/publish', data),
  getPublishedPnLList: (params) => api.get('/accounts/pnl/published', { params }),
  getPublishedPnLDetail: (id) => api.get(`/accounts/pnl/published/${id}`),
  getPnLDrivers: (params) => api.get('/accounts/pnl/drivers', { params }),
  getPnLAlerts: (params) => api.get('/accounts/pnl/alerts', { params }),
  getPnLPulse: (params) => api.get('/accounts/pnl/pulse', { params }),
  getEodPulse: (params) => api.get('/accounts/pnl/eod-pulse', { params }),
  getPnLForecast: (params) => api.get('/accounts/pnl/forecast', { params }),
  getPulseConfig: () => api.get('/accounts/pulse/config'),
  updatePulseConfig: (data) => api.put('/accounts/pulse/config', data),
  triggerTestPulse: (params) => api.post('/accounts/pulse/test', null, { params }),
  triggerDailyPulse: (params) => api.post('/accounts/pulse/run-daily', null, { params }),
  triggerEventPulse: (params) => api.post('/accounts/pulse/run-events', null, { params }),
  getPulseLogs: (params) => api.get('/accounts/pulse/logs', { params }),
  
  // Sync endpoints (for Quick Actions)
  syncPOSSales: (params) => api.post('/accounts/sync/pos-sales', params),
  // (deprecated) syncVendorBills removed — see decommissioned-sync banner above
  // The only allowed manual sync surface is POST /api/accounts/sales-source/backfill
  
  // Sync Watchdog
  getWatchdogStatus: () => api.get('/sync/watchdog-status'),
  
  // Payment Proof
  uploadPaymentProof: (paymentId, data) => api.post(`/accounts/payments/${paymentId}/proof`, data),
  downloadPaymentProof: (paymentId) => api.get(`/accounts/payments/${paymentId}/proof/download`, { responseType: 'blob' }),
  getPaymentProofVersions: (paymentId) => api.get(`/accounts/payments/${paymentId}/proof/versions`),
  
  // Sales Capture Layer (Fallback Ingestion Engine)
  uploadSalesFile: (formData) => api.post('/accounts/sales/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getSalesUploads: (params) => api.get('/accounts/sales/uploads', { params }),
  getSalesUploadDetail: (uploadId) => api.get(`/accounts/sales/uploads/${uploadId}`),
  saveColumnMapping: (uploadId, data) => api.post(`/accounts/sales/uploads/${uploadId}/column-mapping`, data),
  getUploadRows: (uploadId, params) => api.get(`/accounts/sales/uploads/${uploadId}/rows`, { params }),
  getSalesTemplates: () => api.get('/accounts/sales/templates'),
  bootstrapCanonicalItems: () => api.post('/accounts/sales/canonical/bootstrap'),
  getCanonicalItems: (params) => api.get('/accounts/sales/canonical', { params }),
  runItemMapping: (uploadId) => api.post(`/accounts/sales/uploads/${uploadId}/map`),
  getReviewQueue: (uploadId, params) => api.get(`/accounts/sales/uploads/${uploadId}/review`, { params }),
  resolveMapping: (data) => api.post('/accounts/sales/mapping/resolve', data),
  resolveMappingBatch: (data) => api.post('/accounts/sales/mapping/resolve-batch', data),
  backfillCanonicalTax: () => api.post('/accounts/sales/canonical/backfill-tax'),
  activateUpload: (uploadId) => api.post(`/accounts/sales/uploads/${uploadId}/activate`),
  getSalesSourceStatus: (params) => api.get('/accounts/sales/source-status', { params }),

  // Sales Capture Engine (Canonical Read-Only)
  getSalesCaptureSourceStatus: () => api.get('/accounts/sales-source/status'),
  // The ONLY operator-controlled sync surface — labeled as "Backfill", not "Sync".
  // Use for explicit window backfill from Sales Capture into sc_transactions
  // when the scheduler has missed a window. NEVER label this as "Sync Vendors"
  // or "Sync Bills" in UI — those concepts are decommissioned.
  salesSourceBackfill: ({ start_date, end_date, force = true, org_id, outlet_id } = {}) =>
    api.post('/accounts/sales-source/backfill', {
      start_date, end_date, force,
      ...(org_id ? { org_id } : {}),
      ...(outlet_id ? { outlet_id } : {}),
    }),
  getSalesCaptureSummary: (params) => api.get('/accounts/sales-capture/summary', { params }),
  getSalesCaptureTransactions: (params) => api.get('/accounts/sales-capture/transactions', { params }),
  getSalesCaptureTaxes: (params) => api.get('/accounts/sales-capture/taxes', { params }),
  getSalesCaptureChannels: (params) => api.get('/accounts/sales-capture/channels', { params }),
  getSalesCapturePayments: (params) => api.get('/accounts/sales-capture/payments', { params }),
  getSalesCaptureComparison: (params) => api.get('/accounts/sales-capture/comparison', { params }),
  syncSalesCapture: (params) => api.post('/accounts/sync/sales-capture', null, { params }),

  // Migration Control Plane
  getMigrationStatus: () => api.get('/accounts/migration/status'),
  advanceMigrationStage: (data) => api.post('/accounts/migration/advance-stage', data),
  runMigrationHealthCheck: (params) => api.post('/accounts/migration/run-health-check', null, { params }),
  getOperationsSummary: () => api.get('/accounts/migration/operations-summary', { timeout: 30000 }),
  getLegacyAccessLog: (params) => api.get('/accounts/migration/legacy-access-log', { params }),

  // Sale Transaction Upload
  uploadTransactionFile: (formData) => api.post('/accounts/sales/upload-transactions', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  saveTxnColumnMapping: (uploadId, data) => api.post(`/accounts/sales/uploads/${uploadId}/column-mapping-transactions`, data),
  getTxnPreview: (uploadId, params) => api.get(`/accounts/sales/uploads/${uploadId}/txn-preview`, { params }),
  activateTransactionUpload: (uploadId) => api.post(`/accounts/sales/uploads/${uploadId}/activate-transactions`),
  
  // Deletion Workflow (Maker-Checker Governance)
  previewDeletion: (data) => api.post('/accounts/delete-requests/preview', data),
  createDeleteRequest: (data) => api.post('/accounts/delete-requests', data),
  checkEntityDeleteRequest: (params) => api.get('/accounts/delete-requests/check-entity', { params }),
  listDeleteRequests: (params) => api.get('/accounts/delete-requests', { params }),
  getDeleteRequest: (requestId) => api.get(`/accounts/delete-requests/${requestId}`),
  approveDeleteRequest: (requestId) => api.post(`/accounts/delete-requests/${requestId}/approve`),
  rejectDeleteRequest: (requestId, data) => api.post(`/accounts/delete-requests/${requestId}/reject`, data),
  executeDeleteRequest: (requestId) => api.post(`/accounts/delete-requests/${requestId}/execute`),
  cancelDeleteRequest: (requestId) => api.post(`/accounts/delete-requests/${requestId}/cancel`),
  restoreDeleteRequest: (requestId, data) => api.post(`/accounts/delete-requests/${requestId}/restore`, data),

  // Tax Configuration
  getResolvedTaxConfig: () => api.get('/accounts/tax-config'),
  listTaxConfigs: (params) => api.get('/accounts/tax-configs', { params }),
  getTaxConfigHistory: (params) => api.get('/accounts/tax-config/history', { params }),
  createOrgDefaultTaxConfig: (data) => api.post('/accounts/tax-config/org-default', data),
  createOutletTaxOverride: (data) => api.post('/accounts/tax-config/outlet', data),
  updateTaxConfig: (configId, data) => api.put(`/accounts/tax-config/${configId}`, data),
  activateTaxConfig: (configId) => api.post(`/accounts/tax-config/${configId}/activate`),
  deactivateTaxConfig: (configId) => api.post(`/accounts/tax-config/${configId}/deactivate`),
  autoGenerateTaxConfig: () => api.post('/accounts/tax-config/auto-generate'),

  // AI Chat
  chat: (message) => api.post('/accounts/chat', { message }),
  
  // Exports
  generateExport: (data) => api.post('/accounts/exports', data),

  // Transaction Intelligence
  getTransactions: (params) => api.get('/transactions', { params }),
  getTransactionSummary: (params) => api.get('/transactions/summary', { params }),
  getTransaction: (id) => api.get(`/transactions/${id}`),

  // Commission Configs
  getCommissionConfigs: (params) => api.get('/commission-configs', { params }),
  createCommissionConfig: (data) => api.post('/commission-configs', data),
  getCommissionHistory: (params) => api.get('/commission-configs/history', { params }),

  // Partner Agreements
  getAgreements: (params) => api.get('/partner-agreements', { params }),
  createAgreement: (data) => api.post('/partner-agreements', data),

  // Settlement Ingestion
  uploadSettlementFile: (formData) => api.post('/accounts/settlements/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getSettlementUploads: (params) => api.get('/accounts/settlements/uploads', { params }),
  getSettlementUploadDetail: (uploadId) => api.get(`/accounts/settlements/uploads/${uploadId}`),
  saveSettlementColumnMapping: (uploadId, data) => api.post(`/accounts/settlements/uploads/${uploadId}/column-mapping`, data),
  previewSettlementRows: (uploadId, params) => api.get(`/accounts/settlements/uploads/${uploadId}/preview`, { params }),
  activateSettlementUpload: (uploadId, data) => api.post(`/accounts/settlements/uploads/${uploadId}/activate`, data),
  getSettlements: (params) => api.get('/accounts/settlements', { params }),
  getSettlementSummary: (params) => api.get('/accounts/settlements/summary', { params }),
  getSettlementReconciliation: (params) => api.get('/accounts/settlements/reconciliation', { params }),
  getAnomalies: (params) => api.get('/accounts/settlements/anomalies', { params }),
  getAnomalyDrilldown: (bucketKey, params) => api.get(`/accounts/settlements/anomalies/${bucketKey}/drilldown`, { params }),
  // Validation / quarantine admin (Feb 2026 — settlement integrity P0)
  reclassifySettlements: (data) => api.post('/accounts/settlements/admin/reclassify-all', data || {}),
  quarantineSettlement: (settlementId, data) => api.post(`/accounts/settlements/${settlementId}/quarantine`, data),
  restoreSettlement: (settlementId) => api.post(`/accounts/settlements/${settlementId}/restore`),
  getQuarantineState: () => api.get('/accounts/settlements/admin/quarantine-state'),

  // Cash Dashboard
  getDailyCashPosition: (params) => api.get('/accounts/cash/daily-position', { params }),
  getCashDashboard: (params) => api.get('/accounts/cash/dashboard', { params }),
  getPhysicalCashPosition: (params) => api.get('/accounts/cash/physical-position', { params }),
  acknowledgeVariance: (date, data) => api.post(`/accounts/cash/variance/${date}/acknowledge`, data),
  getVarianceReviews: (params) => api.get('/accounts/cash/variance-reviews', { params }),
  
  // Expense Ingestion + Classification
  syncExpenses: () => api.post('/accounts/cash/sync-expenses'),
  getExpensesClassified: (params) => api.get('/accounts/cash/expenses-classified', { params }),
  getExpenseSummary: (params) => api.get('/accounts/cash/expense-summary', { params }),
  getExpenseConfidence: (params) => api.get('/accounts/cash/expense-confidence', { params }),
  getExpenseLedger: (params) => api.get('/accounts/cash/expense-ledger', { params }),

  // Service Charge Ledger
  getServiceChargeSummary: (params) => api.get('/service-charge/summary', { params }),
  getServiceChargeCollections: (params) => api.get('/service-charge/collections', { params }),
  getServiceChargeBalance: () => api.get('/service-charge/balance'),
  getServiceChargeAllocations: (params) => api.get('/service-charge/allocations', { params }),
  getServiceChargeAllocationState: (period, outletId) =>
    api.get('/service-charge/allocation-state', { params: { period, outlet_id: outletId || undefined } }),
  syncServiceChargeAllocation: (period, outletId) =>
    api.post('/service-charge/sync-allocation', { period, outlet_id: outletId || undefined }),
  getServiceChargePayouts: (params) => api.get('/service-charge/payouts', { params }),
  createServiceChargePayout: (data) => api.post('/service-charge/payouts', data),
  reverseServiceChargePayout: (payoutId, data) =>
    api.post(`/service-charge/payouts/${payoutId}/reverse`, data),

  // Settlement Matching
  runSettlementMatching: (data) => api.post('/accounts/settlement-matching/run', data),
  getMatchingSummary: (params) => api.get('/accounts/settlement-matching/summary', { params }),
  getHealthScore: (params) => api.get('/accounts/settlement-matching/health-score', { params }),
  getMatchingResults: (params) => api.get('/accounts/settlement-matching/results', { params }),
  getMatchingExceptions: (params) => api.get('/accounts/settlement-matching/exceptions', { params }),
  getExceptionDetail: (matchId) => api.get(`/accounts/settlement-matching/exceptions/${matchId}`),
  applyMatchOverride: (data) => api.post('/accounts/settlement-matching/override', data),

  // Commission Integrity (powered by settlement charge rows)
  getCommissionIntegrity: (params) => api.get('/accounts/settlements/commission-integrity', { params }),
  getCommissionComparisons: (params) => api.get('/accounts/settlements/commission-comparisons', { params }),

  // Cash Flow Module
  getCashflowOverview: (params) => api.get('/accounts/cashflow/overview', { params }),
  getCashflowLedger: (params) => api.get('/accounts/cashflow/ledger', { params }),
  getMovementDetail: (eventId) => api.get(`/accounts/cashflow/ledger/${eventId}`),
  rebuildCashMovements: (params) => api.post('/accounts/cashflow/rebuild', null, { params }),
  getCashProjectionStatus: (params) => api.get('/accounts/cashflow/projection-status', { params }),
  listCashAccounts: () => api.get('/accounts/cashflow/accounts'),
  createCashAccount: (data) => api.post('/accounts/cashflow/accounts', data),
  updateCashAccount: (accountId, data) => api.put(`/accounts/cashflow/accounts/${accountId}`, data),

  // Cash Flow Reconciliation
  getCashflowReconciliation: (params) => api.get('/accounts/cashflow/reconciliation', { params }),
  confirmMatch: (data) => api.post('/accounts/cashflow/reconciliation/confirm', data),
  markTransfer: (data) => api.post('/accounts/cashflow/reconciliation/mark-transfer', data),
  ignoreEntry: (data) => api.post('/accounts/cashflow/reconciliation/ignore', data),

  // Bank Entries
  addBankEntry: (data) => api.post('/accounts/cashflow/bank-entries', data),
  importBankEntries: (data) => api.post('/accounts/cashflow/bank-entries/import', data),
  listBankEntries: (params) => api.get('/accounts/cashflow/bank-entries', { params }),

  // Obligations & Forecast
  getObligations: () => api.get('/accounts/cashflow/obligations'),
  getCashForecast: (params) => api.get('/accounts/cashflow/forecast', { params }),
  getExpectedInflows: (params) => api.get('/accounts/cashflow/expected-inflows', { params }),

  // Payroll Payout Lines (READ-ONLY consumer of People Engine)
  getPayrollPayoutLines: (cycleId, outletId) =>
    api.get('/accounts/payroll/payout-lines', { params: { cycle_id: cycleId, outlet_id: outletId || undefined } }),
  exportPayrollPayoutLines: (cycleId, outletId) =>
    api.get('/accounts/payroll/payout-lines/export', {
      params: { cycle_id: cycleId, outlet_id: outletId || undefined, format: 'csv' },
      responseType: 'blob',
    }),
  getPayrollPostingStatus: (cycleId) =>
    api.get('/accounts/payroll/payout-lines/posting-status', { params: { cycle_id: cycleId } }),
  syncLockedPayroll: (month, cycleId, outletId) =>
    api.post('/accounts/payroll/ingest-locked-truth', {
      month, cycle_id: cycleId || undefined, outlet_id: outletId || undefined,
    }),
  getPayrollRevisions: (cycleId) =>
    api.get('/accounts/payroll/revisions', { params: { cycle_id: cycleId } }),
  recognisePayroll: (cycleId, outletId) =>
    api.post('/accounts/payroll/recognise-current', {
      cycle_id: cycleId, outlet_id: outletId || undefined,
    }),
  getPayrollAccountingState: (cycleId) =>
    api.get('/accounts/payroll/accounting-state', { params: { cycle_id: cycleId } }),

  // Truth Sparkline (7-day truth-state history)
  getTruthSummary: (outletId, days = 7) =>
    api.get('/accounts/pnl/truth-summary', { params: { outlet_id: outletId, days } }),
};

export default api;
